import { useEffect, useRef, useState } from "react";

// Component Imports
import { AGTable } from "@/components";

// Hooks Imports
import { useAGTable } from "./hooks/useAGTable";
import { useFetchStrategyInstancesQuery } from "../../services/algoManagementApi";
import { useAlerts } from "@/hooks";

// Types Imports
import { IAGAlgoDataRow, useAGTableProps } from "./AlgoTable.types";

// Utils Imports
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { mapRowData } from "./utils/mapRowData";

const AlgoTable = ({
  onInteract,
  interactionsData,
  onInteractToRpc,
}: Pick<
  useAGTableProps,
  "onInteract" | "interactionsData" | "onInteractToRpc"
>) => {
  // States - START --------------------------------------------------------------
  const { showError } = useAlerts();

  const [dataMap, setDataMap] = useState<Map<string, IAGAlgoDataRow>>(
    () => new Map(),
  );

  // Store previous data for comparison
  const previousDataRef = useRef<Map<string, IAGAlgoDataRow>>(new Map());
  // States - END ----------------------------------------------------------------

  // Hooks - START --------------------------------------------------------------
  const {
    gridRef,
    rowData,
    colDefs,
    defaultColDef,
    getContextMenuItems,
    sideBar,
    gridContext,
    handleCellDoubleClicked,
  } = useAGTable({ dataMap, onInteract, interactionsData, onInteractToRpc });
  const {
    data: strategies,
    isLoading: isLoadingStrategies,
    isError: isErrorStrategies,
    error: errorStrategies,
  } = useFetchStrategyInstancesQuery(undefined, {
    pollingInterval: 5000,
    skipPollingIfUnfocused: true,
  });
  // Hooks - END ----------------------------------------------------------------

  // useEffect - START ----------------------------------------------------------
  // Transform and update data incrementally
  useEffect(() => {
    if (!strategies) return;

    const newRows = mapRowData(strategies);
    const newDataMap = new Map<string, IAGAlgoDataRow>();

    // Build new map with unique keys
    newRows.forEach((row) => {
      const key = `${row.connection_id}-${row.strategy_instance_id}`;
      newDataMap.set(key, row);
    });

    setDataMap(newDataMap);

    // Use AG Grid's transaction API for efficient updates
    if (gridRef.current?.api) {
      const api = gridRef.current.api;
      const previousData = previousDataRef.current;

      const toAdd: IAGAlgoDataRow[] = [];
      const toUpdate: IAGAlgoDataRow[] = [];
      const toRemove: IAGAlgoDataRow[] = [];

      // Find new and updated rows
      newDataMap.forEach((newRow, key) => {
        const oldRow = previousData.get(key);
        if (!oldRow) {
          toAdd.push(newRow);
        } else if (JSON.stringify(oldRow) !== JSON.stringify(newRow)) {
          toUpdate.push(newRow);
        }
      });

      // Find removed rows
      previousData.forEach((_, key) => {
        if (!newDataMap.has(key)) {
          toRemove.push({ id: key } as IAGAlgoDataRow);
        }
      });

      // Apply transaction (AG Grid will only update changed rows)
      if (toAdd.length > 0 || toUpdate.length > 0 || toRemove.length > 0) {
        api.applyTransaction({
          add: toAdd,
          update: toUpdate,
          remove: toRemove,
        });

        // Flash updated rows
        if (toUpdate.length > 0) {
          const updatedRowNodes = toUpdate
            .map((row) => api.getRowNode(row.id))
            .filter((node) => node !== undefined);

          if (updatedRowNodes.length > 0) {
            api.flashCells({
              rowNodes: updatedRowNodes,
            });
          }
        }
      }
    }

    previousDataRef.current = newDataMap;
  }, [strategies, gridRef]);

  useEffect(() => {
    if (isErrorStrategies) {
      showError(
        `Failed to fetch Strategy Instances: ${getErrorMessage(errorStrategies)}`,
      );
    }
  }, [isErrorStrategies, errorStrategies, showError]);
  // useEffect - END ------------------------------------------------------------

  return (
    <AGTable
      gridRef={gridRef}
      rowData={rowData}
      colDefs={colDefs}
      defaultColDef={defaultColDef}
      groupDisplayType="singleColumn"
      sideBar={sideBar}
      animateRows={false}
      loading={isLoadingStrategies}
      getContextMenuItems={getContextMenuItems}
      tableHeight={750}
      suppressCellFocus
      suppressColumnVirtualisation
      suppressRowVirtualisation
      suppressRowHoverHighlight
      debounceVerticalScrollbar
      context={gridContext}
      onCellDoubleClicked={handleCellDoubleClicked}
    />
  );
};

export default AlgoTable;
